import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AccountContext } from '@/contexts/account'
import { hasRestaurantOnlyRole } from '@/libs/account'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { RestaurantCancellationHistoryPanel } from '@/components/organisms/restaurant-manager/restaurant-cancellation-history-panel'
import { RestaurantColumnSettingPopover } from '@/components/organisms/restaurant-manager/restaurant-column-setting-popover'
import { RestaurantFilterToolbar } from '@/components/organisms/restaurant-manager/restaurant-filter-toolbar'
import { RestaurantSubjectSummaryPanel } from '@/components/organisms/restaurant-manager/restaurant-subject-summary-panel'
import { RestaurantTableSection } from '@/components/organisms/restaurant-manager/restaurant-table-section'
import { LoadingFull } from '@/components/molecules/loading-full'
import { mainColumnStyle, mainContainerStyle, wholeContainerStyle } from '@/constants/layout'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { setQuery } from '@/features/restaurant-manager/restaurant-manager-slice'
import { reloadAll } from '@/features/restaurant-manager/restaurant-manager-thunks'
import type {
  RestaurantOverviewQuery,
  RestaurantOverviewSnapshot,
  RestaurantOverviewSortKey,
  RestaurantSortOrder,
} from '@/models/restaurant-manager'
import { countUnconfirmedRestaurantCancellations, normalizeRestaurantOverviewQuery } from './helpers'
import { RestaurantHighlightLegend } from '@/components/organisms/restaurant-manager/restaurant-highlight-legend'

export const RestaurantManager: React.FC = () => {
  const { t } = useTranslation()
  const { account, plugins } = useContext(AccountContext)
  const dispatch = useAppDispatch()

  const query = useAppSelector(state => state.restaurantManager.query)
  const snapshot = useAppSelector(state => state.restaurantManager.snapshot)
  const isOverviewLoading = useAppSelector(state => state.restaurantManager.isOverviewLoading)
  const historyGroups = useAppSelector(state => state.restaurantManager.historyGroups)
  const highlightRules = useAppSelector(state => state.restaurantManager.highlightRules)
  const isCancellationLoading = useAppSelector(state => state.restaurantManager.isCancellationLoading)
  const hasCancellationError = useAppSelector(state => state.restaurantManager.hasCancellationError)

  const [reloadCount, setReloadCount] = useState(0)
  const [isCancellationPanelOpen, setIsCancellationPanelOpen] = useState(false)
  const [isSubjectSummaryExpanded, setIsSubjectSummaryExpanded] = useState(false)
  const isRestaurantOnlyRole = hasRestaurantOnlyRole(account)
  const hasMealSpacePlugin = useMemo(
    () =>
      plugins === undefined ||
      checkActivePlugin(plugins, PluginCode.SpaceManagement) ||
      checkActivePlugin(plugins, PluginCode.CheckinTimeSelection),
    [plugins],
  )

  const pendingCancellationCount = useMemo(
    () => (historyGroups ? countUnconfirmedRestaurantCancellations(historyGroups) : null),
    [historyGroups],
  )

  useEffect(() => {
    dispatch(reloadAll(query))
  }, [query, reloadCount, dispatch])

  const updateQuery = useCallback(
    (patch: Partial<RestaurantOverviewQuery> | ((currentQuery: RestaurantOverviewQuery) => Partial<RestaurantOverviewQuery>)) => {
      dispatch(
        setQuery(
          normalizeRestaurantOverviewQuery({
            ...query,
            ...(typeof patch === 'function' ? patch(query) : patch),
          }),
        ),
      )
    },
    [dispatch, query],
  )

  const changeSort = useCallback(
    (sortKey: RestaurantOverviewSortKey, sortOrder: RestaurantSortOrder) => updateQuery({ sortKey, sortOrder }),
    [updateQuery],
  )

  return (
    <div css={wholeContainerStyle}>
      {!isRestaurantOnlyRole && <SideMenu />}
      <div css={[mainColumnStyle, isRestaurantOnlyRole && { width: '100%', marginLeft: 0 }]}>
        <Header title={t('RestaurantManager.PageTitle')} />
        <div css={mainContainerStyle}>
          <div css={pageCardStyle}>
            <RestaurantFilterToolbar
              date={query.date}
              query={query}
              filterOptions={snapshot?.filterOptions}
              hasMealSpacePlugin={hasMealSpacePlugin}
              onMoveDate={date => updateQuery(() => ({ date: date.format('YYYY-MM-DD') }))}
              onReload={() => setReloadCount(c => c + 1)}
              onChangeQuery={updateQuery}
              pendingCancellationCount={pendingCancellationCount}
              onOpenCancellationHistory={() => setIsCancellationPanelOpen(true)}
            />
            <div style={{ borderTop: '1.4px solid #F5F5F5', width: '100%', margin: '12px 0' }} />
            <PageContent
              snapshot={snapshot}
              query={query}
              hasMealSpacePlugin={hasMealSpacePlugin}
              isSubjectSummaryExpanded={isSubjectSummaryExpanded}
              onToggleSummary={() => setIsSubjectSummaryExpanded(c => !c)}
              onChangeSort={changeSort}
            />
          </div>
        </div>
      </div>
      <RestaurantCancellationHistoryPanel
        historyGroups={historyGroups}
        highlightRules={highlightRules}
        isLoadingHistory={isCancellationLoading}
        hasHistoryError={hasCancellationError}
        isOpen={isCancellationPanelOpen}
        onClose={() => setIsCancellationPanelOpen(false)}
      />
      <LoadingFull isLoading={isOverviewLoading} />
    </div>
  )
}

type PageContentProps = {
  snapshot: RestaurantOverviewSnapshot | null
  query: RestaurantOverviewQuery
  hasMealSpacePlugin: boolean
  isSubjectSummaryExpanded: boolean
  onToggleSummary: () => void
  onChangeSort: (sortKey: RestaurantOverviewSortKey, sortOrder: RestaurantSortOrder) => void
}

const PageContent: React.FC<PageContentProps> = ({
  snapshot,
  query,
  hasMealSpacePlugin,
  isSubjectSummaryExpanded,
  onToggleSummary,
  onChangeSort,
}) => {
  const { t } = useTranslation()

  if (!snapshot) {
    return <div css={statusStyle}>{t('No data available')}</div>
  }

  const hasSummary = snapshot.summary.items.length > 0 || snapshot.summary.nightColumns.length > 0

  return (
    <div css={contentStyle}>
      <div css={summaryControlRowStyle}>
        <button
          type="button"
          style={!hasSummary ? { visibility: 'hidden' } : undefined}
          css={summaryToggleButtonStyle}
          onClick={onToggleSummary}
        >
          <span css={summaryToggleIconStyle}>{isSubjectSummaryExpanded ? '▲' : '▼'}</span>
          <span>{t('RestaurantManager.SubjectSummary')}</span>
        </button>
        <div css={{ display: 'flex', alignItems: 'center', gap: 45 }}>
          <RestaurantHighlightLegend />
          <RestaurantColumnSettingPopover defaultColumns={snapshot.displayColumns} hasMealSpacePlugin={hasMealSpacePlugin} />
        </div>
      </div>

      {isSubjectSummaryExpanded && <RestaurantSubjectSummaryPanel summary={snapshot.summary} />}

      {snapshot.rows.length === 0 ? (
        <div css={emptyStateStyle}>{t('No data available')}</div>
      ) : (
        <RestaurantTableSection
          snapshot={snapshot}
          date={query.date}
          sortKey={query.sortKey}
          sortOrder={query.sortOrder}
          onChangeSort={onChangeSort}
        />
      )}
    </div>
  )
}

const pageCardStyle = css({
  background: '#FFF',
  borderRadius: 5,
  boxShadow: '0px 0px 6px #0000001A',
  minHeight: '240px',
  padding: '16px 0 0',
})

const statusStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 18,
  justifyContent: 'center',
  minHeight: '176px',
})

const contentStyle = css({
  color: '#272727',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const summaryControlRowStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0 24px',
})

const summaryToggleButtonStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  color: '#676767',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 'bold',
  gap: 8,
  padding: 0,
})

const summaryToggleIconStyle = css({
  color: '#676767',
  fontSize: 10,
})

const emptyStateStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 18,
  justifyContent: 'center',
  minHeight: '176px',
})
